/**
 * What credits cost and what they buy.
 *
 * These are the provisional packages from the billing design: one rupee a credit, with a
 * discount that grows with the size of the pack. They live here, in one module, because two
 * pages show them and a price that disagrees with itself across a product is worse than a
 * price that is wrong.
 *
 * They are **display copy, not the source of truth**. Nothing here is charged against —
 * every real number in the interface (your balance, your grant, what a turn cost) comes from
 * the server. When payments ship, this table moves behind the API that creates the order, so
 * that a price can never be decided by the browser.
 *
 * The per-question figures are measured, not guessed: see docs/BILLING-DESIGN.md.
 */

export interface CreditPackage {
  id: string
  name: string
  /** Rupees. */
  price: number
  credits: number
  /** Shown as a ribbon on one pack only; two "best value" badges is none. */
  highlight?: string
  summary: string
}

/** The pack every other price is compared against, so the comparison cannot go missing. */
const STARTER: CreditPackage = {
  id: 'starter',
  name: 'Starter',
  price: 99,
  credits: 100,
  summary: 'About twenty questions, or five full pull-request reviews.',
}

export const CREDIT_PACKAGES: CreditPackage[] = [
  STARTER,
  {
    id: 'regular',
    name: 'Regular',
    price: 499,
    credits: 540,
    highlight: 'Best value',
    summary: 'A working month for one engineer reviewing their own pull requests.',
  },
  {
    id: 'team',
    name: 'Team',
    price: 999,
    credits: 1150,
    summary: 'For a small team putting every pull request through a review.',
  },
]

/** What a credit actually buys, from the measured cost of real turns. */
export const WHAT_CREDITS_BUY = [
  { label: 'A pull-request review with suggestions', credits: 25 },
  { label: 'A question about your repositories', credits: 17 },
  { label: 'Create an issue or label in a repository', credits: 13 },
  { label: 'Read issues or labels in repositories', credits: 8 },
]

/**
 * Fallback for pages a signed-out visitor can see, where there is no balance to ask about.
 * Everywhere else the server reports the real grant on `/api/billing/balance`.
 */
export const DEFAULT_FREE_CREDITS = 25

/** Per-credit price, for the "works out at" line. */
export function pricePerCredit(pack: CreditPackage): string {
  return (pack.price / pack.credits).toFixed(2)
}

/** How much cheaper a pack is than buying the same credits at the Starter rate. */
export function savingPercent(pack: CreditPackage): number {
  const baseline = (STARTER.price / STARTER.credits) * pack.credits
  return Math.round(((baseline - pack.price) / baseline) * 100)
}
